import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiClientError,
  createInitiative,
  dismissAttention,
  fetchInitiativeBoard,
  fetchInitiatives,
  refreshInitiativeBrief,
  syncInitiatives,
} from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { AttentionCard } from "../components/mk/AttentionCard";
import { BriefCard } from "../components/mk/BriefCard";
import { GroupRow } from "../components/mk/GroupRow";
import { InitiativeFormModal } from "../components/mk/InitiativeFormModal";
import { Timeline, type ChannelOrAll, type TimelineRange, type TypeOrAll } from "../components/mk/Timeline";
import { Toast } from "../components/mk/Toast";
import { InitiativeDetailPage } from "./InitiativeDetailPage";
import { summarizeSources } from "../lib/mkBoardView";
import { addDaysYmd, summarizeSyncResults, todayJST } from "../lib/mkRules";
import { loadMkCalendarOpen, saveMkCalendarOpen } from "../lib/storage";
import { useToast } from "../lib/useToast";
import type { Board, BoardAttention, CreateInitiativeInput, Initiative, Task } from "../types/contract";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; board: Board };

type CalendarState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; today: string; initiatives: Initiative[]; tasks: Task[] };

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof ApiClientError ? e.message : fallback;
}

/**
 * 施策ページ v2（plan.md §4）: 1カラムで上から
 * AIブリーフ → 判断が必要 → 進行中 → これから → 片付いたもの（折りたたみ）。
 * 状態は worker が自動で片付けるので、画面に「確認済みにする」の押し忘れは出ない。
 */
export function InitiativesPage() {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [briefRefreshing, setBriefRefreshing] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(() => loadMkCalendarOpen());
  const [calendar, setCalendar] = useState<CalendarState>({ status: "idle" });
  const [range, setRange] = useState<TimelineRange>("2w");
  const [channelFilter, setChannelFilter] = useState<ChannelOrAll>("all");
  const [typeFilter, setTypeFilter] = useState<TypeOrAll>("all");
  const { toastMessage, showToast } = useToast();

  const load = useCallback(() => {
    fetchInitiativeBoard()
      .then((board) => setState({ status: "ok", board }))
      .catch((e: unknown) => setState({ status: "error", message: errorMessage(e, "予期しないエラーが発生しました。") }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // カレンダーは開いたときに初めて取得する（既存 fetchInitiatives。閉じている間は通信しない）。
  const loadCalendar = useCallback(() => {
    setCalendar({ status: "loading" });
    fetchInitiatives(addDaysYmd(todayJST(), -35), addDaysYmd(todayJST(), 65))
      .then((res) => setCalendar({ status: "ok", today: res.today, initiatives: res.initiatives, tasks: res.tasks }))
      .catch((e: unknown) => setCalendar({ status: "error", message: errorMessage(e, "予期しないエラーが発生しました。") }));
  }, []);

  useEffect(() => {
    if (calendarOpen && calendar.status === "idle") loadCalendar();
  }, [calendarOpen, calendar.status, loadCalendar]);

  const toggleCalendar = () => {
    setCalendarOpen((prev) => {
      const next = !prev;
      saveMkCalendarOpen(next);
      return next;
    });
  };

  // 外部連携（Meta/LINE/楽天）の即時同期。完了後に board を取り直す（カレンダーも開いていれば取り直す）。
  const handleSync = () => {
    if (syncing) return;
    setSyncing(true);
    syncInitiatives()
      .then((res) => {
        showToast(summarizeSyncResults(res.results));
        load();
        if (calendarOpen) loadCalendar();
      })
      .catch((e: unknown) => showToast(errorMessage(e, "連携の更新に失敗しました。")))
      .finally(() => setSyncing(false));
  };

  const handleRefreshBrief = () => {
    if (briefRefreshing) return;
    setBriefRefreshing(true);
    refreshInitiativeBrief()
      .then((board) => {
        setState({ status: "ok", board });
        showToast("AIブリーフを更新しました");
      })
      .catch((e: unknown) => showToast(errorMessage(e, "AIブリーフの更新に失敗しました。")))
      .finally(() => setBriefRefreshing(false));
  };

  // 楽観的に消し、失敗したら元の位置に戻してトーストを出す。
  const handleDismiss = (id: string) => {
    if (state.status !== "ok") return;
    const index = state.board.attention.findIndex((a) => a.id === id);
    if (index < 0) return;
    const removed: BoardAttention = state.board.attention[index];
    setState((cur) =>
      cur.status === "ok"
        ? { status: "ok", board: { ...cur.board, attention: cur.board.attention.filter((a) => a.id !== id) } }
        : cur
    );
    dismissAttention(id).catch((e: unknown) => {
      setState((cur) => {
        if (cur.status !== "ok" || cur.board.attention.some((a) => a.id === id)) return cur;
        const list = cur.board.attention.slice();
        list.splice(Math.min(index, list.length), 0, removed);
        return { status: "ok", board: { ...cur.board, attention: list } };
      });
      showToast(errorMessage(e, "更新に失敗しました。もう一度お試しください。"));
    });
  };

  const handleCreate = async (input: CreateInitiativeInput) => {
    try {
      const res = await createInitiative(input);
      setShowModal(false);
      showToast(`登録しました。次アクションを ${res.tasks.length} 件自動生成しました`);
      load();
      if (calendarOpen) loadCalendar();
    } catch (e: unknown) {
      showToast(errorMessage(e, "登録に失敗しました"));
    }
  };

  const calendarData = useMemo(() => {
    if (calendar.status !== "ok") return null;
    const initiatives = calendar.initiatives.filter((i) => i.status !== "done");
    const ids = new Set(initiatives.map((i) => i.id));
    return { today: calendar.today, initiatives, tasks: calendar.tasks.filter((t) => ids.has(t.initiative_id)) };
  }, [calendar]);

  if (selectedId) {
    return (
      <InitiativeDetailPage
        id={selectedId}
        onBack={() => {
          setSelectedId(null);
          load();
          if (calendarOpen) loadCalendar();
        }}
      />
    );
  }

  const header = (
    <div className="page-header mk2-header">
      <h1 className="page-title">施策</h1>
      {state.status === "ok" && (
        <div className="mk2-header-right">
          <SyncMini board={state.board} syncing={syncing} onSync={handleSync} />
          <button
            type="button"
            className="mk-btn-outline mk2-header-btn mk-desktop-only"
            aria-pressed={calendarOpen}
            onClick={toggleCalendar}
          >
            カレンダー{calendarOpen ? " ▾" : " ▸"}
          </button>
          <button type="button" className="mk-btn-dark mk2-header-btn" onClick={() => setShowModal(true)}>
            ＋ 登録
          </button>
        </div>
      )}
    </div>
  );

  if (state.status === "loading") {
    return (
      <div className="mk-page mk2-wrap">
        <div className="page-header">
          <h1 className="page-title">施策</h1>
        </div>
        <div className="skeleton" style={{ height: 132, marginBottom: 28 }} />
        <div className="skeleton" style={{ height: 150, marginBottom: 28 }} />
        <div className="skeleton" style={{ height: 200 }} />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="mk-page mk2-wrap">
        <div className="page-header">
          <h1 className="page-title">施策</h1>
        </div>
        <StateBanner kind="error" message={state.message} onRetry={load} />
      </div>
    );
  }

  const { board } = state;
  const { stats } = board;

  return (
    <div className="mk-page mk2-wrap">
      {header}

      {calendarOpen && (
        <section className="mk2-calendar mk-desktop-only" aria-label="カレンダー">
          {calendar.status === "error" && <StateBanner kind="error" message={calendar.message} onRetry={loadCalendar} />}
          {(calendar.status === "loading" || calendar.status === "idle") && (
            <div className="skeleton" style={{ height: 180 }} />
          )}
          {calendarData && (
            <Timeline
              initiatives={calendarData.initiatives}
              tasks={calendarData.tasks}
              today={calendarData.today}
              range={range}
              onRangeChange={setRange}
              channelFilter={channelFilter}
              onChannelFilterChange={setChannelFilter}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              onOpen={setSelectedId}
            />
          )}
        </section>
      )}

      <BriefCard brief={board.brief} refreshing={briefRefreshing} onRefresh={handleRefreshBrief} />

      <section className="mk2-section" aria-label="判断が必要">
        <h2 className="mk2-sec-title">
          判断が必要
          {board.attention.length > 0 && <span className="mk2-sec-count">{board.attention.length}</span>}
        </h2>
        {board.attention.length === 0 ? (
          <p className="mk2-empty">いま判断が必要なものはありません</p>
        ) : (
          <div className="mk2-att-list">
            {board.attention.map((a) => (
              <AttentionCard key={a.id} attention={a} onOpen={setSelectedId} onDismiss={handleDismiss} />
            ))}
          </div>
        )}
      </section>

      <section className="mk2-section" aria-label="進行中">
        <h2 className="mk2-sec-title">
          進行中
          {board.running.length > 0 && <span className="mk2-sec-count">{board.running.length}</span>}
        </h2>
        {board.running.length === 0 ? (
          <p className="mk2-empty">進行中の施策はありません</p>
        ) : (
          <div className="mk2-rows">
            {board.running.map((g) => (
              <GroupRow key={g.key} group={g} variant="running" onOpen={setSelectedId} />
            ))}
          </div>
        )}
      </section>

      <section className="mk2-section" aria-label="これから">
        <h2 className="mk2-sec-title">
          これから
          {board.upcoming.length > 0 && <span className="mk2-sec-count">{board.upcoming.length}</span>}
        </h2>
        {board.upcoming.length === 0 ? (
          <p className="mk2-empty">予定されている施策はありません</p>
        ) : (
          <div className="mk2-rows">
            {board.upcoming.map((g) => (
              <GroupRow key={g.key} group={g} variant="upcoming" onOpen={setSelectedId} />
            ))}
          </div>
        )}
      </section>

      <details className="mk2-closed">
        <summary className="mk2-closed-summary">
          <span className="mk2-closed-title">片付いたもの（直近14日・{stats.closed_14d}件）</span>
          {stats.auto_closed_tasks_14d > 0 && (
            <span className="mk2-closed-sub">うち自動クローズしたタスク {stats.auto_closed_tasks_14d}件</span>
          )}
        </summary>
        {board.closed_recent.length === 0 ? (
          <p className="mk2-empty">直近14日に片付いたものはありません</p>
        ) : (
          <div className="mk2-rows">
            {board.closed_recent.map((g) => (
              <GroupRow key={g.key} group={g} variant="closed" onOpen={setSelectedId} />
            ))}
          </div>
        )}
      </details>

      {showModal && (
        <InitiativeFormModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreate}
          onValidationError={showToast}
        />
      )}

      <Toast message={toastMessage} />
    </div>
  );
}

interface SyncMiniProps {
  board: Board;
  syncing: boolean;
  onSync: () => void;
}

/** ヘッダー右の小さな同期表示。error のソースがあるときだけ赤字で名前を出す。⟳ で今すぐ同期。 */
function SyncMini({ board, syncing, onSync }: SyncMiniProps) {
  const sum = summarizeSources(board.sources, board.today);
  return (
    <span className={`mk2-sync${sum.state === "error" ? " mk2-sync-error" : ""}`}>
      {sum.state === "error" ? (
        <span>同期エラー：{sum.errorLabels.join("・")}</span>
      ) : sum.state === "ok" ? (
        <span>同期 ✓ {sum.at}</span>
      ) : (
        <span>未同期</span>
      )}
      <button
        type="button"
        className="mk2-icon-btn"
        onClick={onSync}
        disabled={syncing}
        aria-label="連携を今すぐ更新"
        title="連携を今すぐ更新"
      >
        <span className={syncing ? "mk2-spin" : undefined} aria-hidden="true">
          ⟳
        </span>
      </button>
    </span>
  );
}
